import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CourseFiltersProps {
  selectedLevel: string[];
  selectedDuration: string[];
  selectedCategory: string[];
  onLevelChange: (levels: string[]) => void;
  onDurationChange: (durations: string[]) => void;
  onCategoryChange: (categories: string[]) => void;
}

export function CourseFilters({
  selectedLevel,
  selectedDuration,
  selectedCategory,
  onLevelChange,
  onDurationChange,
  onCategoryChange
}: CourseFiltersProps) {
  const levels = ['Beginner', 'Intermediate', 'Advanced'];
  const durations = ['0-2 hours', '2-5 hours', '5-10 hours', '10+ hours'];
  const [isExpanded, setIsExpanded] = useState(false);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);

  const activeFilters = selectedCategory.length + selectedLevel.length + selectedDuration.length;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('course_categories')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    loadCategories();
  }, []);

  return (
    <div className="relative bg-background-card rounded-xl border border-gray-800 overflow-hidden">
      {/* Mobile Filter Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-white text-black hover:bg-gray-50 transition-colors md:hidden shadow-sm"
      >
        <div className="flex items-center space-x-3">
          <Filter className="h-4 w-4" />
          <span className="font-medium">Filters</span>
          {activeFilters > 0 && (
            <span className="px-2 py-0.5 text-xs bg-[#800020] text-white rounded-full">
              {activeFilters}
            </span>
          )}
        </div>
        <ChevronDown 
          className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Desktop Filter Header */}
      <div className="hidden md:flex items-center space-x-2 p-4 border-b border-gray-100">
        <Filter className="h-4 w-4 text-gray-400" />
        <h2 className="text-sm font-medium text-black">Filters</h2>
        {activeFilters > 0 && (
          <span className="px-2 py-0.5 text-xs bg-[#800020] text-white rounded-full">
            {activeFilters}
          </span>
        )}
      </div>

      {/* Filter Content */}
      <div 
        className={`
          absolute top-full left-0 right-0 z-50 bg-white border-t border-gray-100
          transform transition-all duration-200 ease-in-out
          ${isExpanded ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0 pointer-events-none'}
          md:transform-none md:opacity-100 md:pointer-events-auto md:static md:border-0
          md:block
          max-h-[calc(100vh-16rem)] overflow-y-auto
        `}
      >
        <div className="space-y-4 p-4">
          <div>
            <h3 className="text-[#d8f477] text-sm font-medium mb-3">Category</h3>
            <div className="space-y-2">
              {categories.map((category) => (
                <label key={category.id} className="flex items-center group">
                  <input
                    type="checkbox"
                    checked={selectedCategory.includes(category.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onCategoryChange([...selectedCategory, category.id]);
                        setIsExpanded(false);
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 300);
                      } else {
                        onCategoryChange(selectedCategory.filter(id => id !== category.id));
                      }
                    }}
                    className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
                  />
                  <span className="ml-2 text-xs text-black">
                    {category.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[#d8f477] text-sm font-medium mb-3">Level</h3>
            <div className="space-y-2">
              {levels.map((level) => (
                <label key={level} className="flex items-center group">
                  <input
                    type="checkbox"
                    checked={selectedLevel.includes(level)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onLevelChange([...selectedLevel, level]);
                        setIsExpanded(false);
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 300);
                      } else {
                        onLevelChange(selectedLevel.filter(l => l !== level));
                      }
                    }}
                    className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
                  />
                  <span className="ml-2 text-xs text-gray-400 group-hover:text-gray-300">
                    {level}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-[#d8f477] text-sm font-medium mb-3">Duration</h3>
            <div className="space-y-2">
              {durations.map((duration) => (
                <label key={duration} className="flex items-center group">
                  <input
                    type="checkbox"
                    checked={selectedDuration.includes(duration)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onDurationChange([...selectedDuration, duration]);
                        setIsExpanded(false);
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 300);
                      } else {
                        onDurationChange(selectedDuration.filter(d => d !== duration));
                      }
                    }}
                    className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
                  />
                  <span className="ml-2 text-xs text-gray-400 group-hover:text-gray-300">
                    {duration}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        
        {/* Mobile Apply Button */}
        <div className="p-4 border-t border-gray-100 md:hidden">
          <button
            onClick={() => setIsExpanded(false)}
            className="w-full px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors"
          >
            Apply Filters {activeFilters > 0 && `(${activeFilters})`}
          </button>
        </div>
      </div>
      
      {/* Mobile Overlay */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
}